"use client";

import * as React from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";

import { DataTablePagination } from "./data-table-pagination";
import { DataTableToolbar } from "./data-table-toolbar";
import { Input } from "../ui/input";
import Actions from "./Actions";
import { Button } from "../ui/button";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  renderToolbarFilters?: (table: any) => React.ReactNode;
  onResetFilters?: () => void;
  onRowClick?: (row: TData) => void;
  leftActions?: React.ReactNode;
  rightActions?: React.ReactNode;
  isCustomFiltered?: boolean;
  pagination?: {
    page: number;
    perPage: number;
    setPage: (page: number) => void;
    setPerPage: (perPage: number) => void;
    total: number;
  };
  sorting?: {
    sort: { field: string; order: "ASC" | "DESC" };
    setSort: (sort: { field: string; order: "ASC" | "DESC" }) => void;
  };
  filterSearch?: string;
  showActionButton?: boolean;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  renderToolbarFilters,
  onResetFilters,
  onRowClick,
  leftActions,
  rightActions,
  pagination,
  isCustomFiltered = false,
  sorting,
  filterSearch,
  showActionButton = false,
}: DataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [sortingState, setSortingState] = React.useState<SortingState>(() => {
    if (sorting) {
      return [{ id: sorting.sort.field, desc: sorting.sort.order === "DESC" }];
    }
    return [];
  });
  const [paginationState, setPaginationState] = React.useState({
    pageIndex: (pagination?.page || 1) - 1, // Convert 1-based to 0-based
    pageSize: pagination?.perPage || 10,
  });

  // Update pagination state when external pagination changes
  React.useEffect(() => {
    if (pagination) {
      setPaginationState((prev) => {
        const newPageIndex = (pagination.page || 1) - 1;
        const newPageSize = pagination.perPage || 10;

        // Only update if values actually changed to prevent unnecessary re-renders
        if (prev.pageIndex !== newPageIndex || prev.pageSize !== newPageSize) {
          return {
            pageIndex: newPageIndex,
            pageSize: newPageSize,
          };
        }
        return prev;
      });
    }
  }, [pagination?.page, pagination?.perPage]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting: sortingState,
      columnVisibility,
      rowSelection,
      columnFilters,
      pagination: paginationState,
    },
    enableRowSelection: true,
    manualPagination: !!pagination,
    manualSorting: !!sorting,
    pageCount: pagination
      ? Math.ceil(pagination.total / Math.max(pagination.perPage, 1))
      : undefined,
    onPaginationChange: (updater) => {
      if (pagination) {
        // Handle both function and object updaters
        const currentState = paginationState;
        const newState =
          typeof updater === "function" ? updater(currentState) : updater;

        // Update local state
        setPaginationState(newState);

        // Update external pagination (convert 0-based to 1-based)
        pagination.setPage(newState.pageIndex + 1);
        if (newState.pageSize !== currentState.pageSize) {
          pagination.setPerPage(newState.pageSize);
        }
      } else {
        // For non-manual pagination, update local state only
        setPaginationState(
          typeof updater === "function" ? updater(paginationState) : updater,
        );
      }
    },
    onRowSelectionChange: setRowSelection,
    onSortingChange: (updater) => {
      if (sorting) {
        // Handle both function and object updaters (like pagination does)
        const currentState = sortingState;
        const newState =
          typeof updater === "function" ? updater(currentState) : updater;

        // Update local state
        setSortingState(newState);

        // Update external sorting if there's a sort column
        if (newState.length > 0) {
          const sortColumn = newState[0];
          sorting.setSort({
            field: sortColumn.id,
            order: sortColumn.desc ? "DESC" : "ASC",
          });
        }
      } else {
        // For non-manual sorting, update local state only
        setSortingState(
          typeof updater === "function" ? updater(sortingState) : updater,
        );
      }
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  return (
    <div className="flex flex-col gap-4">
      <DataTableToolbar
        table={table}
        leftActions={leftActions}
        rightActions={rightActions}
        onResetFilters={onResetFilters}
        hasCustomFilters={isCustomFiltered}
      >
        {renderToolbarFilters ? (
          renderToolbarFilters?.(table)
        ) : (
          <Input
            placeholder={`Filter by ${filterSearch}`}
            onChange={(e: React.FormEvent<HTMLInputElement>) =>
              filterSearch &&
              table
                .getColumn(filterSearch)
                ?.setFilterValue(e.currentTarget.value)
            }
            className="max-w-md"
          />
        )}
      </DataTableToolbar>
      <div className="overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={onRowClick ? "cursor-pointer" : "  "}
                  onClick={(e) => {
                    // dont trigger onRowClick when clicking on a button or link inside the row
                    if ((e.target as HTMLElement).closest("button, a, input")) {
                      return;
                    }
                    onRowClick?.(row.original);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination table={table} />
    </div>
  );
}
